import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AlertTriangle, CreditCard, LogOut } from 'lucide-react';
import { subscriptionPrice } from '../../utils/currency';

// Full-screen, non-dismissable block. Two cases:
//   - expired plan, no grace → renew.
//   - admin set a pay-to-activate plan change → pay to activate.
// The only ways out are to pay (My Subscription) or log out. AppLayout hides it on
// the /mySubscription route so payment is reachable.
//
// The amounts print through utils/currency.js, never a sign typed in here: plans
// are sold in rupees since round 7, and the `pending` block carries its own
// `currency` so a row can say otherwise without this file changing.
const SubscriptionLockModal = () => {
  const navigate = useNavigate();
  const sub = useSelector((s) => s.subscription.mySubscription);
  const pending = sub?.paymentRequired ? sub.pending : null;

  const logout = () => {
    localStorage.clear();
    window.location.replace('/');
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[200] px-4" data-testid="subscription-lock">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center">
        <div className={`mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center ${pending ? 'bg-[#4ABDBD]/15 text-[#2D8A8A]' : 'bg-red-100 text-red-600'}`}>
          {pending ? <CreditCard className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}
        </div>
        <h2 className="text-xl font-bold text-[#1a365d] mb-2">
          {pending ? 'Your plan was changed' : 'Your subscription has expired'}
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          {pending ? (
            <>
              Your administrator set you to <strong>{pending.packageName}</strong>
              {pending.periodLabel ? ` (${pending.periodLabel})` : ''}. Pay <strong>{subscriptionPrice(pending.due, pending, { grouped: true })}</strong> to activate it
              {pending.credit > 0 ? <> — {subscriptionPrice(pending.credit, pending, { grouped: true })} of unused value from your current plan is credited</> : null}.
            </>
          ) : (
            <>
              {sub?.packageName ? <>Your <strong>{sub.packageName}</strong> plan has ended. </> : null}
              Renew or upgrade to keep using Mitra&nbsp;One&nbsp;Care. If you have an appointment to manage,
              your administrator can grant you temporary access.
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => navigate('/mySubscription')}
          data-testid="lock-renew"
          className="w-full px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg flex items-center justify-center gap-2 mb-3"
        >
          <CreditCard className="w-4 h-4" /> {pending ? 'Pay now' : 'Renew now'}
        </button>
        <button
          type="button"
          onClick={logout}
          className="w-full px-4 py-2.5 rounded-xl text-slate-600 font-semibold text-sm bg-gray-100 hover:bg-gray-200 flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </div>
  );
};

export default SubscriptionLockModal;
